import { EngelConfig, EngelTipi, EngelHareket } from '../FikoSPHParametreler';
import { ISPHEleman, ElemanTipi, olusturId } from './ISPHEleman';
import { Vector3 } from 'three';

/**
 * FikoEngel - Obstacle/baffle element for SPH fluid simulation
 * GPU struct size: 128 bytes
 *
 * Layout:
 * - pozX, pozY, pozZ, kalinlik (16 bytes)
 * - yonX, yonY, yonZ, gecirgenlik (16 bytes)
 * - boyutX, boyutY, boyutZ, surukleme (16 bytes)
 * - tip as u32, hareketTip as u32, donusHizi, salGenlik (16 bytes)
 * - salFrekans, aktif as u32, pad, pad (16 bytes)
 * - reserved x3 (48 bytes)
 */
export class FikoEngel implements ISPHEleman {
  readonly id: string;
  readonly tip: ElemanTipi = ElemanTipi.ENGEL;

  pozisyon: Vector3;
  yon: Vector3;
  boyut: Vector3;
  kalinlik: number;
  gecirgenlik: number;
  surukleme: number;
  tipi: EngelTipi;
  hareketTipi: EngelHareket;
  donusHizi: number;
  salinimGenlik: number;
  salinimFrekans: number;
  aktif: boolean;
  ad: string;

  constructor(config: Partial<EngelConfig> = {}) {
    this.id = config.id || olusturId();

    this.pozisyon = config.pozisyon ? new Vector3(config.pozisyon.x, config.pozisyon.y, config.pozisyon.z) : new Vector3(0, 0, 0);
    this.yon = config.yon ? new Vector3(config.yon.x, config.yon.y, config.yon.z).normalize() : new Vector3(0, 0, 1);
    // EngelConfig.boyut is {genislik, yukseklik}, convert to Vector3
    const genislik = config.boyut?.genislik ?? 0.1;
    const yukseklik = config.boyut?.yukseklik ?? 0.1;
    this.boyut = new Vector3(genislik, yukseklik, 0.01);
    this.kalinlik = config.kalinlik ?? 0.01;
    this.gecirgenlik = config.gecirgenlik ?? 0;
    this.surukleme = config.surukleme ?? 0.5;
    this.tipi = config.tipi ?? EngelTipi.DUZ_PLAKA;
    this.hareketTipi = config.hareketli ?? EngelHareket.STATIK;
    this.donusHizi = config.donusHizi ?? 0;
    this.salinimGenlik = config.salınımGenlik ?? 0;
    this.salinimFrekans = config.salınımFrekans ?? 0;
    this.aktif = config.aktif ?? true;
    this.ad = config.ad || 'Engel';
  }

  toGPUBytes(): ArrayBuffer {
    const buffer = new ArrayBuffer(128);
    const view = new DataView(buffer);
    let offset = 0;

    // Chunk 1: pozX, pozY, pozZ, kalinlik (16 bytes)
    view.setFloat32(offset, this.pozisyon.x, true); offset += 4;
    view.setFloat32(offset, this.pozisyon.y, true); offset += 4;
    view.setFloat32(offset, this.pozisyon.z, true); offset += 4;
    view.setFloat32(offset, this.kalinlik, true); offset += 4;

    // Chunk 2: yonX, yonY, yonZ, gecirgenlik (16 bytes)
    view.setFloat32(offset, this.yon.x, true); offset += 4;
    view.setFloat32(offset, this.yon.y, true); offset += 4;
    view.setFloat32(offset, this.yon.z, true); offset += 4;
    view.setFloat32(offset, this.gecirgenlik, true); offset += 4;

    // Chunk 3: boyutX, boyutY, boyutZ, surukleme (16 bytes)
    view.setFloat32(offset, this.boyut.x, true); offset += 4;
    view.setFloat32(offset, this.boyut.y, true); offset += 4;
    view.setFloat32(offset, this.boyut.z, true); offset += 4;
    view.setFloat32(offset, this.surukleme, true); offset += 4;

    // Chunk 4: tip as u32, hareketTip as u32, donusHizi, salGenlik (16 bytes)
    // Map string enums to numeric for GPU
    const tipiNumeric = this.tipi === EngelTipi.DUZ_PLAKA ? 0 : this.tipi === EngelTipi.EGRILI ? 1 : this.tipi === EngelTipi.SILINDIR ? 2 : 3;
    const hareketNumeric = this.hareketTipi === EngelHareket.STATIK ? 0 : this.hareketTipi === EngelHareket.DONUYOR ? 1 : 2;
    view.setUint32(offset, tipiNumeric, true); offset += 4;
    view.setUint32(offset, hareketNumeric, true); offset += 4;
    view.setFloat32(offset, this.donusHizi, true); offset += 4;
    view.setFloat32(offset, this.salinimGenlik, true); offset += 4;

    // Chunk 5: salFrekans, aktif as u32, pad, pad (16 bytes)
    view.setFloat32(offset, this.salinimFrekans, true); offset += 4;
    view.setUint32(offset, this.aktif ? 1 : 0, true); offset += 4;
    view.setUint32(offset, 0, true); offset += 4; // pad
    view.setUint32(offset, 0, true); offset += 4; // pad

    // Chunks 6-8: reserved (48 bytes)
    for (let i = 0; i < 12; i++) {
      view.setUint32(offset, 0, true);
      offset += 4;
    }

    return buffer;
  }

  gpuStructBoyut(): number {
    return 128;
  }

  toJSON(): any {
    return {
      id: this.id,
      tip: this.tip,
      pozisyon: { x: this.pozisyon.x, y: this.pozisyon.y, z: this.pozisyon.z },
      yon: { x: this.yon.x, y: this.yon.y, z: this.yon.z },
      boyut: { genislik: this.boyut.x, yukseklik: this.boyut.y },
      kalinlik: this.kalinlik,
      gecirgenlik: this.gecirgenlik,
      surukleme: this.surukleme,
      tipi: this.tipi,
      hareketli: this.hareketTipi,
      donusHizi: this.donusHizi,
      salınımGenlik: this.salinimGenlik,
      salınımFrekans: this.salinimFrekans,
      aktif: this.aktif,
      ad: this.ad
    };
  }

  dogrula(): { gecerli: boolean; hatalar: string[] } {
    const hatalar: string[] = [];

    if (this.boyut.x <= 0 || this.boyut.y <= 0 || this.boyut.z <= 0) {
      hatalar.push('Boyutlar pozitif olmalıdır');
    }

    if (this.kalinlik <= 0) {
      hatalar.push('Kalınlık pozitif olmalıdır');
    }

    if (this.gecirgenlik < 0 || this.gecirgenlik > 1) {
      hatalar.push('Geçirgenlik 0-1 arasında olmalıdır');
    }

    if (this.surukleme < 0 || this.surukleme > 1) {
      hatalar.push('Sürükleme katsayısı 0-1 arasında olmalıdır');
    }

    if (this.yon.lengthSq() < 0.99 || this.yon.lengthSq() > 1.01) {
      hatalar.push('Yön vektörü normalize edilmeli');
    }

    if (this.hareketTipi === EngelHareket.DONUYOR && this.donusHizi === 0) {
      hatalar.push('Dönüş hareketi için dönüş hızı belirtilmeli');
    }

    if (this.hareketTipi === EngelHareket.SALINIYOR) {
      if (this.salinimGenlik <= 0) {
        hatalar.push('Salınım hareketi için genlik pozitif olmalıdır');
      }
      if (this.salinimFrekans <= 0) {
        hatalar.push('Salınım hareketi için frekans pozitif olmalıdır');
      }
    }

    return {
      gecerli: hatalar.length === 0,
      hatalar
    };
  }

  klonla(): FikoEngel {
    return new FikoEngel({
      pozisyon: this.pozisyon.clone(),
      yon: this.yon.clone(),
      boyut: { genislik: this.boyut.x, yukseklik: this.boyut.y },
      kalinlik: this.kalinlik,
      gecirgenlik: this.gecirgenlik,
      surukleme: this.surukleme,
      tipi: this.tipi,
      hareketli: this.hareketTipi,
      donusHizi: this.donusHizi,
      salınımGenlik: this.salinimGenlik,
      salınımFrekans: this.salinimFrekans,
      aktif: this.aktif,
      ad: this.ad + ' (Kopya)'
    });
  }

  parametreListesi(): string[] {
    return [
      'pozisyon', 'yon', 'boyut', 'kalinlik', 'gecirgenlik',
      'surukleme', 'tipi', 'hareketTipi', 'donusHizi',
      'salinimGenlik', 'salinimFrekans', 'aktif', 'ad'
    ];
  }

  parametreAyarla(anahtar: string, deger: any): boolean {
    switch (anahtar) {
      case 'pozisyon':
      case 'yon':
      case 'boyut':
        if (deger instanceof Vector3) {
          if (anahtar === 'yon') {
            this.yon.copy(deger).normalize();
          } else {
            (this as any)[anahtar].copy(deger);
          }
          return true;
        }
        break;
      case 'kalinlik':
      case 'gecirgenlik':
      case 'surukleme':
      case 'donusHizi':
      case 'salinimGenlik':
      case 'salinimFrekans':
        if (typeof deger === 'number') {
          (this as any)[anahtar] = deger;
          return true;
        }
        break;
      case 'tipi':
        // Accept both string and number for enum assignment
        (this as any).tipi = deger;
        return true;
      case 'hareketTipi':
        // Accept both string and number for enum assignment
        (this as any).hareketTipi = deger;
        return true;
      case 'aktif':
        if (typeof deger === 'boolean') {
          this.aktif = deger;
          return true;
        }
        break;
      case 'ad':
        if (typeof deger === 'string') {
          this.ad = deger;
          return true;
        }
        break;
    }
    return false;
  }

  /**
   * Get a parameter value by name
   */
  parametreAl(anahtar: string): any {
    return (this as any)[anahtar];
  }
}

/**
 * Factory function to create default obstacle element
 */
export function varsayilanEngel(config?: Partial<EngelConfig>): FikoEngel {
  return new FikoEngel(config);
}
